/**
 * BANK-IBAN-R3.7 / R3.9 — customer checkout flags (Mini App).
 * VITE flags are compile guards only. Rendered checkout uses Worker bootstrap.
 * Production Vite recipe must not set BANK_IBAN_ONLY.
 */
import type { PaymentMethodChoice } from "./checkout-validation-v2.js";

export const BANK_IBAN_ONLY_MODE = "BANK_IBAN_ONLY";

type RuntimePayment = {
  mode: string;
  allowed_methods: PaymentMethodChoice[];
  rail?: string | null;
};

const g = globalThis as typeof globalThis & {
  __AS_CUSTOMER_BOOTSTRAP_PAYMENT?: RuntimePayment | null;
};

export function setRuntimeCustomerPayment(payment: RuntimePayment | null): void {
  g.__AS_CUSTOMER_BOOTSTRAP_PAYMENT = payment;
}

export function getRuntimeCustomerPayment(): RuntimePayment | null {
  return g.__AS_CUSTOMER_BOOTSTRAP_PAYMENT ?? null;
}

function envBag(env?: Record<string, string>): Record<string, string> {
  return env ?? ((import.meta as { env?: Record<string, string> }).env ?? {});
}

function envFlag(name: string, defaultEnabled: boolean, env?: Record<string, string>): boolean {
  const raw = String(envBag(env)[name] ?? "").trim().toLowerCase();
  if (!raw) return defaultEnabled;
  if (raw === "false" || raw === "0" || raw === "off") return false;
  if (raw === "true" || raw === "1" || raw === "on") return true;
  return defaultEnabled;
}

export function resolveCustomerPaymentMode(env?: Record<string, string>): string {
  const runtime = !env ? getRuntimeCustomerPayment()?.mode : null;
  if (runtime) return String(runtime).trim().toUpperCase();
  return String(envBag(env).VITE_CUSTOMER_PAYMENT_MODE ?? "").trim().toUpperCase();
}

export function isBankIbanOnlyMode(env?: Record<string, string>): boolean {
  return resolveCustomerPaymentMode(env) === BANK_IBAN_ONLY_MODE;
}

export function isBankIbanCustomerPaymentsEnabled(env?: Record<string, string>): boolean {
  if (isBankIbanOnlyMode(env)) return true;
  return envFlag("VITE_BANK_IBAN_CUSTOMER_PAYMENTS", true, env);
}

export function isNovapayCustomerPaymentsEnabled(env?: Record<string, string>): boolean {
  if (isBankIbanOnlyMode(env)) return false;
  return envFlag("VITE_NOVAPAY_CUSTOMER_PAYMENTS", true, env);
}

export function isCodCustomerPaymentsEnabled(env?: Record<string, string>): boolean {
  if (isBankIbanOnlyMode(env)) return false;
  return envFlag("VITE_COD_CUSTOMER_PAYMENTS", true, env);
}

export function enabledCustomerCheckoutPaymentMethods(
  env?: Record<string, string>,
): PaymentMethodChoice[] {
  const runtime = !env ? getRuntimeCustomerPayment() : null;
  if (runtime?.allowed_methods?.length) return runtime.allowed_methods;
  if (isBankIbanOnlyMode(env)) return ["bank_transfer_fop"];
  const out: PaymentMethodChoice[] = [];
  if (isNovapayCustomerPaymentsEnabled(env)) out.push("novapay_card");
  if (isBankIbanCustomerPaymentsEnabled(env)) out.push("bank_transfer_fop");
  if (isCodCustomerPaymentsEnabled(env)) out.push("nova_poshta_cod");
  return out;
}

export function isSingleBankIbanCheckout(env?: Record<string, string>): boolean {
  const enabled = enabledCustomerCheckoutPaymentMethods(env);
  return enabled.length === 1 && enabled[0] === "bank_transfer_fop";
}

export function compiledCustomerPaymentFlags(env?: Record<string, string>): {
  CUSTOMER_PAYMENT_MODE: string;
  BANK_IBAN: boolean;
  NOVAPAY: boolean;
  COD: boolean;
} {
  return {
    CUSTOMER_PAYMENT_MODE: resolveCustomerPaymentMode(env) || "UNSET",
    BANK_IBAN: isBankIbanCustomerPaymentsEnabled(env),
    NOVAPAY: isNovapayCustomerPaymentsEnabled(env),
    COD: isCodCustomerPaymentsEnabled(env),
  };
}

/** Staging customer build must expose BANK_IBAN only. */
export function assertStagingExclusiveBankIban(env?: Record<string, string>): void {
  const flags = compiledCustomerPaymentFlags(env);
  const enabled = enabledCustomerCheckoutPaymentMethods(env);
  if (
    flags.CUSTOMER_PAYMENT_MODE !== BANK_IBAN_ONLY_MODE ||
    flags.BANK_IBAN !== true ||
    flags.NOVAPAY !== false ||
    flags.COD !== false ||
    enabled.length !== 1 ||
    enabled[0] !== "bank_transfer_fop"
  ) {
    throw new Error(
      `staging customer configuration must expose only BANK_IBAN; got ${JSON.stringify({ flags, enabled })}`,
    );
  }
}
